import {provide, inject, type InjectionKey, type App, onMounted, onUnmounted, getCurrentInstance} from "vue";
import {FormEnvironment, EventBus, EventBusRegistry, EventBusListener, CommandHandler, CommandRegistry, UnregisterHandler, CatchAllCommandsRegistry} from "./Types";
import {UserInfo} from "@t/user/UserInfo.ts";

const eventBusKey = Symbol() as InjectionKey<EventBus>;


export let env:FormEnvironment = null;

export function setGlobalEnvironment(e:FormEnvironment) {
	if (env) throw new Error("Attempt to install environment object twice");
	env = e;
}


/** Register an event bus to the root of an application */
export function provideEventBus(app:App, eb?:EventBus):EventBus {
	const bus:EventBus = eb == null ? GLOBAL_EVENT_BUS : eb;
    app.provide(eventBusKey, bus);
    return bus;
}


export function connectEventBus(newScope:string|null = null):EventBus {
	let bus = inject(eventBusKey, GLOBAL_EVENT_BUS);
	if (newScope) {
		//console.log("Connecting new bus: " + newScope + " with parent " + bus.name);
		bus = createEventBus(newScope, bus);
		provide(eventBusKey, bus);
	}
	return bus;
}

function debugArgs(...args:any[]):string {
	let b = "";
	for (let a of args) {
		let val:string;
		if (typeof a === 'undefined') val = "undefined";
		else if (a === null) val = "null";
		else if (typeof a === 'string') val = "'" + a + "'";
		else if (typeof a === 'object') val = JSON.stringify(a);
		else val.toString();
		b += ", " + val;
	}
	return b;
}

const DOES_NOTHING = () => {};

class EventBusImpl implements EventBus {
	readonly name:string;
	readonly parent:EventBus | null;

	private readonly registry: EventBusRegistry;
	private readonly commands:CommandRegistry;
	private readonly catchAllCommands:CatchAllCommandsRegistry;

	private nextReg:number;
	private handlerUid:number;
	private debug:boolean;

	constructor(name:string, parent:EventBus|null = null) {
		this.name = name;
		this.parent = parent;

		this.registry = {};
		this.commands = {};
		this.catchAllCommands = [];
		this.nextReg = 1;
		this.handlerUid = 0;
	}

	debugging(flag = true) {
		this.debug = flag;
		return this;
	}


	on(eventName:string, listener: EventBusListener): number {
		const inComponent = !!getCurrentInstance();
		if (this.registry[eventName] === undefined) this.registry[eventName] = [];
		const regNum = this.nextReg++;
		this.registry[eventName].push({ callback: listener, regNum });
		if (this.debug) console.log(this.name + ".on(" + eventName + ")");
		if (inComponent) {
			onUnmounted(() => this.off(regNum));
			return 0;
		} else {
			return regNum;
		}
	}

	off(subscription: number):boolean {
		if (subscription <= 0) return;	// not a valid subscription
		for (let eventName of Object.keys(this.registry)) {
			const listeners = this.registry[eventName];
			if (listeners === undefined) continue;
			for (let i = 0; i < listeners.length; i++) {
				if (listeners[i].regNum === subscription) {
					listeners.splice(i, 1);
					if (this.debug) console.log(this.name + ".off(" + eventName + ")");
					return true;
				}
			}
		}
		console.warn("EventBus was asked to remove listener with unknown ID " + subscription);
		return false;
	}

	fire(eventName: string, ...args:any[]):void {
		const listeners = this.registry[eventName];
		if (this.debug) {
			if (listeners == null || listeners.length === 0) console.log("No listeners for event " + eventName + " on bus " + this.name);
		}
		if (listeners) {
			for (let listener of [...listeners]) {
				if (this.debug) console.log(this.name + ".fire(" + eventName + debugArgs(...args) + ")");
				const stopBubbling = listener.callback.call(this, ...args);
				if (stopBubbling === true) return;
			}
		}
		if (this.parent) this.parent.fire(eventName, ...args);	// call parent bus
	}


	private oneOnCommand(command: string, handler: CommandHandler):number {
		if (typeof this.commands[command] === 'undefined') this.commands[command] = [];
		const uid = this.handlerUid++;
		this.commands[command].push({uid, handler});
		if (this.debug) console.log(this.name + ".onCmd(" + command + ")");
		return uid;
	}

	onCommand(command: string|string[], handler: CommandHandler):UnregisterHandler {
		const inComponent = !!getCurrentInstance();
		const uids:number[] = [];
		if (Array.isArray(command)) {
			for (let c of command) {
				uids.push(this.oneOnCommand(c, handler));
			}
		} else {
			uids.push(this.oneOnCommand(command, handler));
		}
		if (inComponent) {
			onUnmounted(() => { this.unregisterCommands(uids); });
			return DOES_NOTHING;
		} else {
			return () => { this.unregisterCommands(uids); };
		}
	}

	onAnyCommand(handler: CommandHandler):UnregisterHandler {
		const inComponent = !!getCurrentInstance();
		const uid = this.handlerUid++;
		this.catchAllCommands.push({ uid, handler });
		if (this.debug) console.log(this.name + ".onCmd(*)");
		if (inComponent) {
			onUnmounted(() => { this.unregisterCatchAll(uid); });
			return DOES_NOTHING;
		} else {
			return () => { this.unregisterCatchAll(uid); };
		}
	}


	async executeCommand(command: string, ...args: any[]):Promise<boolean> {
		const regs = this.commands[command];
		if (Array.isArray(regs)) {
			const regsCopy = [...regs];
			for (let i = regsCopy.length - 1; i >= 0; i--) {
				const reg = regsCopy[i];
				if (this.debug) console.log(this.name + ".command(" + command + debugArgs(...args) + ")");
				const result = await reg.handler.call(this, command, ...args);
				if (result != "pass") return result === "ok";
			}
		}
		// Not a concrete handler - try catch-all
		const catchAll = [...this.catchAllCommands];
		for (let i = catchAll.length - 1; i >= 0; i--) {
			const reg = catchAll[i];
			if (this.debug) console.log(this.name + ".allCommands(" + command + debugArgs(...args) + ")");
			const result = await reg.handler.call(this, command, ...args);
			if (result != "pass") return result === "ok";
		}
		if (this.parent) return await this.parent.executeCommand(command, ...args);
		return false;
	}


	private unregisterCommands(uids: number[]):void {
		for (let uid of uids) {
			for (let command of Object.keys(this.commands)) {
				const regs = this.commands[command];
				for (let i = regs.length-1; i >= 0; i--) {
					if (regs[i].uid === uid) {
						if (this.debug) console.log(this.name + ".offCmd(" +uid+"/"+ command + ")");
						regs.splice(i, 1);
					}
				}
			}
		}
	}

	private unregisterCatchAll(uid: number):void {
		const regs = this.catchAllCommands;
		for (let i = regs.length-1; i >= 0; i--) {
			if (regs[i].uid === uid) {
				if (this.debug) console.log(this.name + ".offAllCmd("+uid+")");
				regs.splice(i, 1);
			}
		}
	}
}


export function createEventBus(name:string, parent:EventBus|null = null):EventBus {
    return new EventBusImpl(name, parent);
}

/**
 * Global event bus, one instance per application/page
 */
const GLOBAL_EVENT_BUS:EventBus = createEventBus("GLOBAL");

export function injectEnv():FormEnvironment {
    if (!env) throw "Unknown environment (You must call setGlobalEnvironment)";
    return env;
}

export function userInRole(user: UserInfo, role:string) {
	if (Array.isArray(user.roles)) {
		return user.roles.indexOf(role) >= 0;
	}
	return false;
}

export function userInAllRoles(user: UserInfo, ...roles:string[]) {
	for (let r of roles) {
		if (!userInRole(user, r)) return false;
	}
	return true;
}

export function userInAnyRole(user: UserInfo, ...roles:string[]) {
	for (let r of roles) {
		if (userInRole(user, r)) return true;
	}
	return false;
}
